/**
 * MetricSlider Component
 *
 * A reusable slider component for collecting 1-10 scale metrics with visual feedback.
 * Features gradient fill, Lucide icon indicators, descriptive labels, and mobile-friendly controls.
 *
 * Supports both Lucide icon components (preferred) and legacy emoji strings.
 *
 * @component
 * @example
 * ```tsx
 * <MetricSlider
 *   label="Soreness Level"
 *   icon={Dumbbell}
 *   min={1}
 *   max={10}
 *   value={soreness}
 *   onChange={setSoreness}
 *   minLabel="None"
 *   maxLabel="Extreme"
 *   helpText="How sore are you feeling?"
 * />
 * ```
 */

import React, { useState } from 'react';
import type { LucideIcon } from 'lucide-react';

interface MetricSliderProps {
  /** Main label for the metric (e.g., "Soreness Level") */
  label: string;

  /** Lucide icon component to display next to the label */
  icon?: LucideIcon;

  /** Legacy emoji string — ignored when `icon` is provided */
  emoji?: string;

  /** Minimum value on the scale */
  min: number;

  /** Maximum value on the scale */
  max: number;

  /** Current value */
  value: number;

  /** Callback when value changes */
  onChange: (value: number) => void;

  /** Label for minimum value (e.g., "None", "Very Low") */
  minLabel: string;

  /** Label for maximum value (e.g., "Extreme", "Very High") */
  maxLabel: string;

  /** Optional help text explaining the metric */
  helpText?: string;
}

/**
 * Get descriptive label based on value position in range.
 * Divides range into thirds: lower third = minLabel, middle = "Moderate", upper = maxLabel
 */
function getDescriptiveLabel(value: number, min: number, max: number, minLabel: string, maxLabel: string): string {
  const range = max - min;
  const third = range / 3;

  if (value <= min + third) return minLabel;
  if (value <= min + (third * 2)) return 'Moderate';
  return maxLabel;
}

export const MetricSlider: React.FC<MetricSliderProps> = ({
  label,
  icon: Icon,
  emoji,
  min,
  max,
  value,
  onChange,
  minLabel,
  maxLabel,
  helpText,
}) => {
  const [localValue, setLocalValue] = useState(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value);
    setLocalValue(newValue);
    onChange(newValue);
  };

  // Calculate percentage for gradient fill
  const percentage = ((localValue - min) / (max - min)) * 100;

  // Get descriptive label for current value
  const descriptiveLabel = getDescriptiveLabel(localValue, min, max, minLabel, maxLabel);

  return (
    <div className="mb-6">
      {/* Label with icon */}
      <div className="flex items-center mb-2">
        {Icon ? (
          <Icon className="w-5 h-5 mr-2" style={{ color: 'var(--text-secondary)' }} />
        ) : emoji ? (
          <span className="text-2xl mr-2" role="img" aria-label={label}>
            {emoji}
          </span>
        ) : null}
        <label className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          {label}
        </label>
      </div>

      {/* Help Text */}
      {helpText && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {helpText}
        </p>
      )}

      {/* Slider */}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          value={localValue}
          onChange={handleChange}
          className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--accent-ui-focus)] focus:ring-offset-2"
          style={{
            background: `linear-gradient(to right, #667eea 0%, #667eea ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`
          }}
          aria-label={`${label} slider`}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={localValue}
          aria-valuetext={`${localValue} - ${descriptiveLabel}`}
        />

        {/* Min/Max Labels */}
        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mt-2">
          <span>{min} - {minLabel}</span>
          <span>{max} - {maxLabel}</span>
        </div>

        {/* Current Value Display */}
        <div className="mt-3 text-center">
          <span className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
            Current: {localValue}
          </span>
          <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
            ({descriptiveLabel})
          </span>
        </div>
      </div>
    </div>
  );
};
